import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  FiBriefcase,
  FiCalendar,
  FiDownload,
  FiEye,
  FiFilter,
  FiSearch,
  FiStar,
  FiX,
  FiUser,
  FiUsers,
  FiCheckCircle,
} from "react-icons/fi";
import { FaCalendarAlt } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL } from "../utils/config";
import "../index.css";
import "./EmployeeDashboard.css";
import "./EmployeeLeaves.css";


const ShortJobApplicants = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [roleSearchQuery, setRoleSearchQuery] = useState("");
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [roles, setRoles] = useState([]);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const roleDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);

  const clientId = localStorage.getItem("clientId");

  useEffect(() => {
    if (clientId) {
      fetchApplications();
    }
    fetchRoles();

    const handleClickOutside = (event) => {
      if (
        roleDropdownRef.current &&
        !roleDropdownRef.current.contains(event.target)
      ) {
        setIsRoleDropdownOpen(false);
      }
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setIsStatusDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [clientId]);

  const fetchRoles = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/jobs/all/${clientId}`);
      if (res.data.success) {
        const jobs = res.data.jobPosts || res.data.data || [];
        const shortJobs = jobs.filter((job) => job.jobType === "short");
        const formattedRoles = shortJobs.map((job, index) => ({
          _id: job._id || index,
          name: job.role,
        }));
        const uniqueRoles = Array.from(
          new Set(formattedRoles.map((r) => r.name))
        )
          .filter(Boolean)
          .map((name) => formattedRoles.find((r) => r.name === name));
        setRoles(uniqueRoles);
      }
    } catch (err) {
      console.error("Failed to fetch roles:", err);
    }
  };

  const fetchApplications = async () => {
    if (!clientId) return;

    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/applications/all/${clientId}`);
      if (res.data.success) {
        const allApps = res.data.applications || [];

        // Filter for short jobs
        const shortApps = allApps.filter((app) => {
          const job = app.jobId;
          if (job && job.jobType === "short") return true;
          if (app.jobType === "short") return true;
          if (job && (job.startTime || job.endTime || job.totalDuration)) {
            return true;
          }
          return false;
        });

        setApplications(shortApps);
      } else {
        setApplications([]);
      }
    } catch (err) {
      console.error("Failed to fetch applications:", err);
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDocumentUrl = (filePath) => {
    if (!filePath) return "";
    const relativePath = filePath.includes("uploads")
      ? "uploads/" + filePath.split(/uploads[\\/]/).pop().replace(/\\/g, "/")
      : filePath.replace(/\\/g, "/");
    return `${API_BASE_URL.replace("/api", "")}/${relativePath}`;
  };

  const handleOpenModal = (app) => {
    setSelectedApplicant(app);
    setIsModalOpen(true);
  };

  const handleOpenAssessmentModal = (app) => {
    window.location.href = `/score?search=${encodeURIComponent(
      app.firstName || ""
    )}`;
  };

  const handleUpdateScore = async (id, field, value) => {
    const numericValue = field === "status" ? value : Number(value);

    try {
      const res = await axios.post(`${API_BASE_URL}/applications/update-score`, {
        applicationId: id,
        [field]: numericValue,
      });
      if (res.data.success) {
        setApplications((prev) =>
          prev.map((app) =>
            app._id === id ? { ...app, [field]: numericValue } : app
          )
        );
        toast.success("Score updated successfully!");
      }
    } catch (err) {
      console.error("Update score error:", err);
      toast.error("Failed to update score.");
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setRoleFilter("");
    setDateFilter("");
    setStatusFilter("");
    setRoleSearchQuery("");
    setCurrentPage(1);
  };

  const filteredApplications = applications.filter((app) => {
    const query = searchQuery.toLowerCase();
    const matchesSearch =
      !query ||
      `${app.firstName || ""} ${app.lastName || ""}`.toLowerCase().includes(query) ||
      (app.jobId?.role || "").toLowerCase().includes(query) ||
      (app.mobile || "").toLowerCase().includes(query) ||
      (app.email || "").toLowerCase().includes(query);

    const matchesRole = roleFilter
      ? (app.jobId?.role || "").toLowerCase() === roleFilter.toLowerCase()
      : true;

    let matchesDate = true;
    if (dateFilter) {
      const appDate = new Date(app.appliedAt).toISOString().split("T")[0];
      matchesDate = appDate === dateFilter;
    }

    const matchesStatus = statusFilter
      ? (app.status || "Applied").toLowerCase() === statusFilter.toLowerCase()
      : true;

    return matchesSearch && matchesRole && matchesDate && matchesStatus;
  });

  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage) || 1;
  const paginatedApplications = filteredApplications.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const downloadCSV = () => {
    if (filteredApplications.length === 0) {
      toast.info("No applicant records to download!");
      return;
    }

    const headers = [
      "Applicant ID",
      "Candidate Name",
      "Email",
      "Mobile",
      "Applied Short Role",
      "Technical Score",
      "Status",
      "Applied Date",
    ];

    const csvRows = [
      headers.join(","),
      ...filteredApplications.map((app) => {
        return [
          `"${app._id || ""}"`,
          `"${(app.firstName || "") + " " + (app.lastName || "")}"`,
          `"${app.email || ""}"`,
          `"${app.mobile || ""}"`,
          `"${(app.jobId?.role || "Short Job").replace(/"/g, '""')}"`,
          app.technicalScore || 0,
          `"${app.status || "Applied"}"`,
          `"${app.appliedAt ? new Date(app.appliedAt).toLocaleDateString("en-IN") : "-"}"`,
        ].join(",");
      }),
    ];

    const csvData = csvRows.join("\n");
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `short_job_applicants_${new Date()
      .toLocaleDateString("en-IN")
      .replace(/\//g, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getPeriodLabel = () => {
    return new Date().toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "selected":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "rejected":
        return "bg-rose-50 text-rose-700 border-rose-200";
      case "pending":
        return "bg-amber-50 text-amber-700 border-amber-200";
      default:
        return "bg-indigo-50 text-indigo-700 border-indigo-200";
    }
  };

  const todayCount = applications.filter((a) => {
    const today = new Date().toDateString();
    return new Date(a.appliedAt).toDateString() === today;
  }).length;

  if (!clientId) {
    return (
      <div className="emp-dash">
        <main className="grid place-items-center min-h-[60vh] p-4">
          <div className="emp-dash__card max-w-[480px] w-full p-8 text-center">
            <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mx-auto mb-3">
              <FiUser size={24} />
            </div>
            <h3 className="text-base font-bold text-gray-900">Authentication Required</h3>
            <p className="text-xs text-gray-500 mt-1">Please login to access short job applications.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: "white" }} className="emp-dash">
      <ToastContainer position="top-right" autoClose={2500} hideProgressBar={false} />
      <main>
        {/* Header */}
        <div className="emp-dash__header">
          <div className="flex items-baseline gap-3 flex-wrap">
            <h1 className="emp-dash__greeting text-lg sm:text-xl font-bold whitespace-nowrap">
              Short Job <span>Applicants</span>
            </h1>
            <span className="text-xs text-slate-400 font-medium hidden sm:inline">
              Manage, screen, and review short-term / gig candidate applications.
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="emp-dash__date-pill">
              <FaCalendarAlt />
              <span>{getPeriodLabel()}</span>
            </div>
            <button
              type="button"
              onClick={downloadCSV}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold uppercase tracking-wider transition-all cursor-pointer"
            >
              <FiDownload size={14} /> Export CSV
            </button>
          </div>
        </div>

        {/* Top KPI Metrics Strip */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Total Short Applicants</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--indigo">
                <FiUsers />
              </div>
            </div>
            <div className="emp-dash__stat-value">{applications.length}</div>
            <div className="emp-dash__stat-meta">gig role submissions</div>
          </div>

          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Selected Hires</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--emerald">
                <FiCheckCircle />
              </div>
            </div>
            <div className="emp-dash__stat-value text-emerald-700">
              {applications.filter((a) => a.status === "Selected").length}
            </div>
            <div className="emp-dash__stat-meta">candidates accepted</div>
          </div>

          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Rejected</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--rose">
                <FiX />
              </div>
            </div>
            <div className="emp-dash__stat-value text-rose-600">
              {applications.filter((a) => a.status === "Rejected").length}
            </div>
            <div className="emp-dash__stat-meta">not shortlisted</div>
          </div>

          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Filtered Matches</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--violet">
                <FiFilter />
              </div>
            </div>
            <div className="emp-dash__stat-value text-violet-700">
              {filteredApplications.length}
            </div>
            <div className="emp-dash__stat-meta">matching active filters</div>
          </div>

          <div className="emp-dash__stat col-span-2 lg:col-span-1">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Applied Today</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--rate">
                <FiCalendar />
              </div>
            </div>
            <div className="emp-dash__stat-value text-indigo-700">
              {todayCount}
            </div>
            <div className="emp-dash__stat-meta">new today</div>
          </div>
        </div>

        {/* Filter Toolbar Card */}
        <div className="emp-dash__card p-4 mb-6">
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
            {/* Search & Filters */}
            <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
              {/* Search */}
              <div className="relative min-w-[200px] flex-1 max-w-[280px]">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                <input
                  type="text"
                  placeholder="Search name, phone, role, email..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-8 pr-3 py-2 text-xs border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white font-medium"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FiX size={12} />
                  </button>
                )}
              </div>

              {/* Role Filter */}
              <div className="relative" ref={roleDropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsRoleDropdownOpen((prev) => !prev)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl border transition-all bg-white whitespace-nowrap cursor-pointer ${
                    roleFilter
                      ? "border-indigo-500 text-indigo-700 bg-indigo-50"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <FiBriefcase className="text-gray-400 text-xs" />
                  <span className="truncate max-w-[120px]">
                    {roleFilter || "All Short Roles"}
                  </span>
                  <span className="text-gray-400 text-[10px]">▾</span>
                </button>

                {isRoleDropdownOpen && (
                  <div className="absolute z-50 mt-1.5 w-56 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto p-1">
                    <div className="p-1.5 border-b border-gray-100 sticky top-0 bg-white">
                      <input
                        type="text"
                        placeholder="Search roles..."
                        value={roleSearchQuery}
                        onChange={(e) => setRoleSearchQuery(e.target.value)}
                        className="w-full py-1 px-2 text-xs bg-gray-50 border border-gray-200 rounded-lg outline-none focus:bg-white focus:border-indigo-600"
                        autoFocus
                      />
                    </div>
                    <div
                      onClick={() => {
                        setRoleFilter("");
                        setIsRoleDropdownOpen(false);
                        setCurrentPage(1);
                      }}
                      className={`px-3 py-1.5 text-xs rounded-lg cursor-pointer transition-colors ${
                        !roleFilter
                          ? "bg-indigo-50 text-indigo-700 font-bold"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      All Short Roles
                    </div>
                    {roles
                      .filter((r) =>
                        r.name.toLowerCase().includes(roleSearchQuery.toLowerCase())
                      )
                      .map((r) => (
                        <div
                          key={r._id}
                          onClick={() => {
                            setRoleFilter(r.name);
                            setIsRoleDropdownOpen(false);
                            setCurrentPage(1);
                          }}
                          className={`px-3 py-1.5 text-xs rounded-lg cursor-pointer transition-colors ${
                            roleFilter === r.name
                              ? "bg-indigo-50 text-indigo-700 font-bold"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {r.name}
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* Status Filter */}
              <div className="relative" ref={statusDropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsStatusDropdownOpen((prev) => !prev)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl border transition-all bg-white whitespace-nowrap cursor-pointer ${
                    statusFilter
                      ? "border-indigo-500 text-indigo-700 bg-indigo-50"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <FiCheckCircle className="text-gray-400 text-xs" />
                  <span className="capitalize">{statusFilter || "All Statuses"}</span>
                  <span className="text-gray-400 text-[10px]">▾</span>
                </button>

                {isStatusDropdownOpen && (
                  <div className="absolute z-50 mt-1.5 w-44 bg-white border border-gray-200 rounded-xl shadow-xl p-1">
                    {["", "Selected", "Pending", "Rejected", "Applied"].map((st) => (
                      <div
                        key={st}
                        onClick={() => {
                          setStatusFilter(st);
                          setIsStatusDropdownOpen(false);
                          setCurrentPage(1);
                        }}
                        className={`px-3 py-1.5 text-xs rounded-lg cursor-pointer capitalize transition-colors ${
                          statusFilter === st
                            ? "bg-indigo-50 text-indigo-700 font-bold"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {st || "All Statuses"}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Date Filter */}
              <div className="relative">
                <input
                  type="date"
                  value={dateFilter}
                  onChange={(e) => {
                    setDateFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="h-[34px] px-2.5 py-1 text-xs border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white font-medium cursor-pointer"
                />
              </div>
            </div>

            {/* Clear Action */}
            {(searchQuery || roleFilter || dateFilter || statusFilter) && (
              <button
                type="button"
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2 text-xs font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all whitespace-nowrap cursor-pointer self-end lg:self-center"
              >
                <FiX size={13} /> Reset Filters
              </button>
            )}
          </div>
        </div>

        {/* Short Job Applicants Table Card */}
        <div className="emp-dash__card p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                <FiStar size={15} className="text-indigo-600" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                  Short Job Applicants Directory
                </h2>
                <p className="text-[10px] text-gray-500">
                  Showing {paginatedApplications.length} of {filteredApplications.length} candidates
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span className="font-medium">Rows per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 text-xs font-semibold outline-none focus:border-indigo-600"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto rounded-xl border border-gray-200/80">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-gray-50/90 border-b border-gray-200 text-gray-600 uppercase text-[10px] tracking-wider font-bold">
                  <th className="py-3.5 px-4 w-12 text-center">S.No</th>
                  <th className="py-3.5 px-4 min-w-[220px]">Candidate Details</th>
                  <th className="py-3.5 px-4 min-w-[150px]">Short Job Role</th>
                  <th className="py-3.5 px-4 min-w-[140px]">Contact Info</th>
                  <th className="py-3.5 px-4 text-center min-w-[120px]">Score</th>
                  <th className="py-3.5 px-4 text-center min-w-[120px]">Status</th>
                  <th className="py-3.5 px-4 text-center min-w-[150px]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-8 h-8 border-3 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-3" />
                        <span className="text-xs text-gray-500 font-medium">
                          Loading candidate profiles...
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : filteredApplications.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-12 h-12 rounded-xl bg-gray-50 text-gray-300 flex items-center justify-center mb-2">
                          <FiStar size={24} />
                        </div>
                        <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                          No Short Job Applicants Found
                        </p>
                        <p className="text-[10px] text-gray-400 mt-1">
                          No applicants matched the selected filters.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedApplications.map((app, index) => {
                    const fullName = `${app.firstName || ""} ${app.lastName || ""}`.trim() || "Candidate";
                    const initial = (app.firstName?.charAt(0) || "C").toUpperCase();
                    const score = app.technicalScore || 0;

                    return (
                      <tr
                        key={app._id}
                        className="hover:bg-indigo-50/20 transition-colors group"
                      >
                        {/* Index */}
                        <td className="py-3.5 px-4 text-center text-gray-400 font-medium text-[11px]">
                          {(currentPage - 1) * itemsPerPage + index + 1}
                        </td>

                        {/* Profile */}
                        <td className="py-3.5 px-4">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-amber-500 to-indigo-600 text-white flex items-center justify-center font-bold text-xs flex-shrink-0 mt-0.5 shadow-xs">
                              {initial}
                            </div>
                            <div className="min-w-0">
                              <p className="font-bold text-gray-900 text-xs truncate">
                                {fullName}
                              </p>
                              <div className="flex items-center gap-1.5 text-[10px] text-gray-500 mt-0.5">
                                <span>{app.email || "No email"}</span>
                                <span>•</span>
                                <span>
                                  {app.appliedAt
                                    ? new Date(app.appliedAt).toLocaleDateString("en-IN", {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                      })
                                    : "-"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Short Role */}
                        <td className="py-3.5 px-4">
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                            <FiStar size={11} className="text-amber-500" />
                            <span className="truncate max-w-[130px]">
                              {app.jobId?.role || app.role || "Short Gig"}
                            </span>
                          </span>
                        </td>

                        {/* Contact */}
                        <td className="py-3.5 px-4 text-gray-600 text-[11px]">
                          <div className="font-medium">{app.mobile || "N/A"}</div>
                          <span className="text-[10px] text-gray-400">
                            {app.currentLocation || "Location not set"}
                          </span>
                        </td>

                        {/* Technical Score Input */}
                        <td className="py-3.5 px-4 text-center">
                          <div className="inline-flex items-center gap-1">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              className="w-14 px-1.5 py-1 border border-gray-200 rounded-lg text-xs font-bold text-center focus:border-indigo-600 focus:bg-white outline-none transition-colors"
                              value={app.technicalScore ?? 0}
                              onChange={(e) =>
                                handleUpdateScore(
                                  app._id,
                                  "technicalScore",
                                  e.target.value
                                )
                              }
                            />
                            <span
                              className={`text-[10px] font-extrabold ${
                                score >= 80
                                  ? "text-emerald-600"
                                  : score >= 50
                                  ? "text-amber-600"
                                  : "text-rose-600"
                              }`}
                            >
                              %
                            </span>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="py-3.5 px-4 text-center">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider border ${getStatusBadge(
                              app.status
                            )}`}
                          >
                            {app.status || "Applied"}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="py-3.5 px-4 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleOpenModal(app)}
                              className="px-2.5 py-1.5 bg-indigo-50 hover:bg-indigo-600 hover:text-white text-indigo-700 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all flex items-center gap-1 cursor-pointer"
                              title="View Details"
                            >
                              <FiEye size={12} /> View
                            </button>

                            <button
                              type="button"
                              onClick={() => handleOpenAssessmentModal(app)}
                              className="px-2.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all shadow-xs flex items-center gap-1 cursor-pointer"
                              title="Evaluate Quiz & Score"
                            >
                              <FiStar size={12} /> Score
                            </button>

                            {app.resume && (
                              <button
                                type="button"
                                onClick={() =>
                                  window.open(
                                    formatDocumentUrl(app.resume),
                                    "_blank"
                                  )
                                }
                                className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors cursor-pointer"
                                title="Download Resume"
                              >
                                <FiDownload size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
              <span className="text-xs text-gray-500">
                Page <strong className="text-gray-900">{currentPage}</strong> of{" "}
                <strong className="text-gray-900">{totalPages}</strong>
              </span>

              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-xs font-bold rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => setCurrentPage(num)}
                    className={`w-7 h-7 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                      currentPage === num
                        ? "bg-indigo-600 text-white shadow-xs"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    {num}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    setCurrentPage((p) => Math.min(p + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-xs font-bold rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Candidate Details Modal */}
      {isModalOpen && selectedApplicant && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="px-6 py-5 bg-gradient-to-r from-amber-50 via-white to-indigo-50 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500 text-white flex items-center justify-center font-bold text-sm shadow-md shadow-amber-500/20">
                  {(selectedApplicant.firstName?.charAt(0) || "C").toUpperCase()}
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">
                    {selectedApplicant.firstName} {selectedApplicant.lastName}
                  </h2>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700">
                    {selectedApplicant.jobId?.role || selectedApplicant.role || "Short Job Applicant"} · Applied {new Date(selectedApplicant.appliedAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center transition-colors cursor-pointer"
              >
                <FiX size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-6 overflow-y-auto flex-1 custom-scrollbar">
              {/* Contact Details */}
              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                  Candidate Contact Information
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <p className="text-[9px] font-bold text-gray-400 uppercase">Email</p>
                    <p className="text-xs font-bold text-gray-900 mt-0.5 truncate">
                      {selectedApplicant.email || "N/A"}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <p className="text-[9px] font-bold text-gray-400 uppercase">Mobile</p>
                    <p className="text-xs font-bold text-gray-900 mt-0.5">
                      {selectedApplicant.mobile || "N/A"}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <p className="text-[9px] font-bold text-gray-400 uppercase">Location</p>
                    <p className="text-xs font-bold text-gray-900 mt-0.5">
                      {selectedApplicant.currentLocation || "N/A"}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <p className="text-[9px] font-bold text-gray-400 uppercase">Notice Period</p>
                    <p className="text-xs font-bold text-indigo-600 mt-0.5">
                      {selectedApplicant.noticePeriod || "Immediate"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Education & Experience */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50/70 rounded-xl border border-gray-100 space-y-2">
                  <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Education
                  </h4>
                  <div className="text-xs space-y-1 text-gray-600">
                    <p><strong>Degree:</strong> {selectedApplicant.highestQualification || "N/A"}</p>
                    <p><strong>Institution:</strong> {selectedApplicant.institution || "N/A"}</p>
                    <p><strong>Department:</strong> {selectedApplicant.department || "N/A"}</p>
                    <p><strong>Percentage:</strong> {selectedApplicant.percentage ? `${selectedApplicant.percentage}%` : "N/A"}</p>
                  </div>
                </div>

                <div className="p-4 bg-gray-50/70 rounded-xl border border-gray-100 space-y-2">
                  <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Experience & Background
                  </h4>
                  <div className="text-xs space-y-1 text-gray-600">
                    <p><strong>Total Experience:</strong> {selectedApplicant.experience || 0} Years</p>
                    <p><strong>Company:</strong> {selectedApplicant.companyName || "N/A"}</p>
                    <p><strong>Current CTC:</strong> {selectedApplicant.currentCTC || "N/A"}</p>
                    <p><strong>Expected CTC:</strong> {selectedApplicant.expectedCTC || "N/A"}</p>
                  </div>
                </div>
              </div>

              {/* Skills */}
              {selectedApplicant.skills && (
                <div>
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                    Skills & Competencies
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedApplicant.skills.split(",").map((s, i) => (
                      <span
                        key={i}
                        className="px-2.5 py-1 bg-amber-50 text-amber-800 text-xs font-medium rounded-lg border border-amber-200"
                      >
                        {s.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Scores */}
              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                  Assessment Scores
                </h3>
                <div className="grid grid-cols-4 gap-3 text-center">
                  <div className="p-3 bg-amber-50/50 rounded-xl border border-amber-100">
                    <p className="text-[10px] text-gray-500 font-bold uppercase">Tech Test</p>
                    <p className="text-sm font-extrabold text-amber-800 mt-1">
                      {selectedApplicant.technicalScore || 0}%
                    </p>
                  </div>
                  <div className="p-3 bg-amber-50/50 rounded-xl border border-amber-100">
                    <p className="text-[10px] text-gray-500 font-bold uppercase">Appearance</p>
                    <p className="text-sm font-extrabold text-amber-800 mt-1">
                      {selectedApplicant.appearanceScore || 0}%
                    </p>
                  </div>
                  <div className="p-3 bg-amber-50/50 rounded-xl border border-amber-100">
                    <p className="text-[10px] text-gray-500 font-bold uppercase">Knowledge</p>
                    <p className="text-sm font-extrabold text-amber-800 mt-1">
                      {selectedApplicant.workKnowledge || 0}%
                    </p>
                  </div>
                  <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                    <p className="text-[10px] text-emerald-800 font-bold uppercase">Overall</p>
                    <p className="text-sm font-extrabold text-emerald-700 mt-1">
                      {selectedApplicant.overallRating || 0}%
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
              {selectedApplicant.resume ? (
                <button
                  type="button"
                  onClick={() =>
                    window.open(
                      formatDocumentUrl(selectedApplicant.resume),
                      "_blank"
                    )
                  }
                  className="py-2 px-4 rounded-xl bg-amber-50 text-amber-800 hover:bg-amber-100 text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-1.5 cursor-pointer"
                >
                  <FiDownload size={13} /> View Resume
                </button>
              ) : (
                <span className="text-xs text-gray-400 italic">No resume file</span>
              )}

              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="py-2 px-5 rounded-xl bg-slate-900 hover:bg-black text-white text-xs font-bold uppercase tracking-wider transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default ShortJobApplicants;